// useBreakpoint — the single source of truth for "are we on a phone?" across
// every platform primitive. Iron Rule 11: responsive behaviour lives in the
// shared primitives, not per-page. The primitives that CSS can't reach (the
// inline-styled VirtualTable, SuggestionRail) branch on this hook instead.
//
// 768px matches the sole breakpoint hard-coded in apps/terminal mobile.css
// (kept in sync via the MOBILE_BREAKPOINT constant — import it there if that
// file is ever converted from a magic number).
//
// Implementation: matchMedia + useSyncExternalStore so there's no tearing on
// resize and it's SSR/first-paint safe (getServerSnapshot returns the desktop
// value, so a server render never assumes mobile).

import { useSyncExternalStore } from 'react';

export const MOBILE_BREAKPOINT = 768;

const QUERY = `(max-width: ${MOBILE_BREAKPOINT}px)`;

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mql = window.matchMedia(QUERY);
  // Safari <14 only supports the deprecated addListener signature.
  if (mql.addEventListener) {
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }
  mql.addListener(onChange);
  return () => mql.removeListener(onChange);
}

function getSnapshot(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(QUERY).matches;
}

function getServerSnapshot(): boolean {
  // Desktop-first: never assume mobile on the server / first paint.
  return false;
}

/** True when the viewport is at or below the mobile breakpoint (≤768px). */
export function useIsMobile(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
